import { ReactNode } from 'react';
import { Moment } from 'moment';
import { Resources } from '@erecruit/types';
import { ResolverResponseOverride } from '@erecruit/modules';
import { RouteComponentProps } from 'react-router-dom';
import { Insightful } from '../../types';
import { closePositionRecord, fetchPositionRecordInfo } from './state/reducer';

import { PositionField } from '../AddNewPositionWizard/types';
import { SupportedTypes } from './helpers';

export interface PositionRecordParams {
  id: string;
  matchStatus?: keyof PositionRecordMatchCounters;
}

interface PositionRecordActions {
  closePositionRecord: typeof closePositionRecord;
  fetchPositionRecordInfo: typeof fetchPositionRecordInfo;
}

export interface PositionRecordProps
  extends RouteComponentProps<PositionRecordParams>,
    PositionRecordActions,
    Insightful {
  infoSections?: PositionInfoSections;
  isClosingPosition: boolean;
  isLoadingInfo: boolean;
}

export enum MutationType {
  aboutPosition = 'UpdatePosition',
  location = 'UpdatePositionLocation',
  schedule = 'UpdatePositionSchedule',
  billing = 'UpdatePositionBilling',
  additionalInfo = 'UpdateCustomField',
}

export interface BaseSectionProps<T> {
  data: T;
  isMyOwn: boolean;
  expanded?: boolean;
  onEdit(section: PositionSection): void;
}

export interface PositionValues {
  [field: string]: number | string | Moment | number[];
}

export interface PositionRecordMutation {
  section: PositionSection;
  positionValues: PositionValues;
}

export interface FormSpec {
  section: PositionSection;
  title: ReactNode;
  description: ReactNode;
  fields: PositionField[];
}

export interface ModalConfig {
  id: string;
  title: ReactNode;
  formSpec: FormSpec;
  data: SupportedTypes;
}

export interface PositionInfoSections {
  aboutPosition: Resources.Position;
}

export interface PositionRecordSections extends PositionInfoSections {
  location: Resources.PositionLocation;
  schedule: Resources.PositionSchedule;
  billing: Resources.PositionBilling;
  approvalPath: Resources.ApprovalPath;
  approvalRejectionReasons: Resources.ApprovalRejectionReason[];
  additionalInfo: Resources.CustomField[];
}

export interface PositionRecordMatchCounters {
  submittals: number;
  interview: number;
  offers: number;
  candidateAccepted: number;
  fullPlacement: number;
}

export type PositionSection = keyof Omit<
  PositionRecordSections,
  'approvalPath' | 'approvalRejectionReasons'
>;

export type PositionRecordInfoResponse = [
  ResolverResponseOverride<Resources.Position>,
];

export type PositionRecordResponse = [
  ResolverResponseOverride<Resources.PositionLocation>,
  ResolverResponseOverride<Resources.PositionSchedule>,
  ResolverResponseOverride<Resources.PositionBilling>,
  ResolverResponseOverride<Resources.MatchSearchCount>,
  ResolverResponseOverride<Resources.MatchSearchCount>,
  ResolverResponseOverride<Resources.MatchSearchCount>,
  ResolverResponseOverride<Resources.MatchSearchCount>,
  ResolverResponseOverride<Resources.MatchSearchCount>,
  ResolverResponseOverride<Resources.ApprovalPath>,
  ResolverResponseOverride<Resources.ApprovalRejectionReason>,
  ResolverResponseOverride<Resources.CustomField>,
];

export type MatchParams = {
  id: string;
  matchStatus: keyof PositionRecordMatchCounters;
};
